import { ContentState, EditorState } from "draft-js";
import { FC, useEffect, useState } from "react";
import { Editor } from "react-draft-wysiwyg";
import style from "./style.module.scss";

import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

import { convertToRaw, Modifier } from "draft-js";
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from "html-to-draftjs";

const fonts = [
  "Arial",
  "Georgia",
  "Impact",
  "Tahoma",
  "Times New Roman",
  "Helvetica",
  "Calibri",
  "Futura",
  "Roboto",
  "Garamond",
  "Verdana",
  "Proxima Nova",
].sort();

const options = {
  options: [
    "inline",
    "fontSize",
    "blockType",
    "fontFamily",
    "colorPicker",
    "emoji",
    // "embedded",
    "textAlign",
    "list",
    "link",
    // "image",
    "remove",
    "history",
  ],
  fontFamily: {
    options: fonts,
  },
  textAlign: {
    inDropdown: false,
    className: undefined,
    component: undefined,
    dropdownClassName: undefined,
    options: ["left", "center", "right", "justify"],
  },
};

// Utility function to convert current editor content to semantically correct HTML
export const exportToHtml = (editorState: EditorState | undefined) => {
  if (!editorState) return '';
  const rawContent = convertToRaw(editorState.getCurrentContent());

  // Inject text-align into each block's inline style before conversion
  rawContent.blocks = rawContent.blocks.map(block => {
    const textAlign = block.data?.textAlign;
    if (textAlign && ['left', 'center', 'right', 'justify'].includes(textAlign)) {
      return {
        ...block,
        data: {
          ...block.data,
          'text-align': textAlign
        }
      };
    }
    return block;
  });

  return draftToHtml(rawContent);
};

// Utility: Convert <br> separators inside blocks into multiple blocks
function splitBlocksFromBrs(html: string): string {
  // Convert <hN> ... <br> ... </hN> into <hN>...</hN><p>...</p>
  html = html.replace(/<h([1-6])>([\s\S]*?)<br ?\/?>([\s\S]*?)<\/h\1>/gi, (match, level, first, rest) => {
    // Split first and rest by <br>
    let [heading, ...others] = (first + '<br>' + rest).split(/<br ?\/?>(?:\s*<br ?\/?>)*/i);
    let result = `<h${level}>${heading.trim()}</h${level}>`;
    if (others.length > 0) {
      result += others.map(str => `<p>${str.trim()}</p>`).join('');
    }
    return result;
  });
  // Convert <p> ... <br> ... </p> into multiple <p>
  html = html.replace(/<p>([\s\S]*?)<br ?\/?>([\s\S]*?)<\/p>/gi, (match, first, rest) => {
    const parts = (first + '<br>' + rest).split(/<br ?\/?>(?:\s*<br ?\/?>)*/i).filter(x => x.trim() !== '');
    return parts.map(str => `<p>${str.trim()}</p>`).join('');
  });
  return html;
}

export interface RichTextViewerProps {
  onChange?: (content: EditorState) => void;
  initialState?: string;
  readonly?: boolean;
}

export const RichTextEditor: FC<RichTextViewerProps> = ({
  initialState,
  onChange,
  readonly = false,
}) => {
  const [editorState, setEditorState] = useState<EditorState | undefined>(
    undefined
  );

  const handleEditorStateChange = (editorState: any) => {
    setEditorState(editorState);
    onChange && onChange(editorState);
  };

  useEffect(() => {
    if (!initialState) {
      setEditorState(undefined);
      return;
    }

    // Pre-process legacy HTML to ensure proper blocks
    const processedState = splitBlocksFromBrs(initialState);

    // Parse HTML into DraftJS blocks
    const blocksFromHtml = htmlToDraft(processedState);
    const { contentBlocks, entityMap } = blocksFromHtml;
    let contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);

    // Create a temp div to extract HTML element info for mapping
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = initialState;
    const htmlElements = tempDiv.querySelectorAll(
      "p, div, h1, h2, h3, h4, h5, h6, blockquote"
    );

    // Map block types and text alignment per section
    let newContentState = contentState;
    const blockMap = contentState.getBlockMap();
    let blockIndex = 0;

    blockMap.forEach((block, blockKey) => {
      if (block && blockIndex < htmlElements.length) {
        const el = htmlElements[blockIndex] as HTMLElement;
        // Robustly detect alignment from style attribute (including justify)
        let textAlign = el.style.textAlign;
        if (!textAlign) {
          // Fall back to parsing style string manually
          const styleAttr = el.getAttribute('style') || '';
          const match = styleAttr.match(/text-align\s*:\s*(left|center|right|justify)/i);
          textAlign = match ? match[1].toLowerCase() : '';
        }

        // Create a selection for just this block
        const selection = EditorState.createWithContent(newContentState)
          .getSelection()
          .merge({
            anchorKey: blockKey,
            focusKey: blockKey,
            anchorOffset: 0,
            focusOffset: block.getLength(),
          });

        let newData = block.getData();
        if (["left", "center", "right", "justify"].includes(textAlign)) {
          newData = newData.set("textAlign", textAlign);
        }

        newContentState = Modifier.setBlockData(newContentState, selection, newData);
        blockIndex++;
      }
    });

    // Only create the editorState once, after all updates
    const newEditorState = EditorState.createWithContent(newContentState);
    setEditorState(newEditorState);
  }, [initialState]);


  return (
    <Editor
      editorClassName={style.editor}
      wrapperClassName={style.editorContainer}
      toolbarClassName={style.toolbar}
      toolbar={options}
      toolbarHidden={readonly}
      readOnly={readonly}
      editorState={editorState}
      onEditorStateChange={handleEditorStateChange}
      textAlignment="left"
    />
  );
};
